import {Show, Text, TextSize, ThemeColorPath, ThemeColorTextPath} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {EMPTY_PLACEHOLDER, suffixTestId} from 'shared/web';

import {LevelFill} from '../LevelInput/LevelFill';
import {EMPTY_LABEL, FULL_LABEL} from '../LevelInput/levelLabels';
import {LevelValueProps} from '../LevelInput/LevelValueProps';
import {formatPercent} from '../LevelInput/utils/formatPercent';
import {getLevelBandColor} from '../LevelInput/utils/getLevelBandColor';
import {resolveLevel} from '../LevelInput/utils/resolveLevel';

/**
 * How the glyph is coloured. `banded` repeats the band colours of the `FuelLevel`
 * input; `neutral` paints the whole glyph in the secondary text colour, for rows
 * that have to read as text — an entity header parameter, for example.
 */
type FuelLevelValueTone = 'banded' | 'neutral';

export interface FuelLevelValueProps extends LevelValueProps {
  /**
   * Size of the percentage readout. The default `small` (20px line box) is the reviewed
   * design; `xSmall` (16px line box) is for callers that must fit the glyph into the line
   * box of an `xSmall` text row, such as an `Attributes` row.
   */
  readoutSize?: Extract<TextSize, 'small' | 'xSmall'>;
  /** Defaults to `banded`. */
  tone?: FuelLevelValueTone;
}

const DEFAULT_READOUT_SIZE = 'small';
const DEFAULT_TONE = 'banded';
const EDGE_LABEL_SIZE = 'xxSmall';
/** At an 8px bar height a 4px radius already draws semicircular ends, as the input pill does. */
const BAR_RADIUS = 'small';
/** Fill colour of the `neutral` tone — the colour the surrounding header text is drawn in. */
const NEUTRAL_FILL_COLOR: ThemeColorPath = 'text.secondary';
/** The input paints `E` red and `F` secondary; the neutral tone drops the red with the bands. */
const EMPTY_LABEL_COLOR: Record<FuelLevelValueTone, ThemeColorTextPath> = {
  banded: 'danger',
  neutral: 'secondary',
};
const FULL_LABEL_COLOR: ThemeColorTextPath = 'secondary';

const rootVariants = cva('inline-flex items-center gap-1 align-middle');
const glyphVariants = cva('inline-flex flex-none items-center gap-1');
const barVariants = cva(
  'relative box-border h-[calc(var(--eag-space-step,0.25)*2rem)] w-[calc(var(--eag-space-step,0.25)*16rem)] flex-none overflow-hidden rounded-[var(--radius-small)] border',
  {
    variants: {
      isRecorded: {
        true: 'border-solid border-[var(--color-palettes-neutral-40-100)] bg-[var(--color-palettes-neutral-30-100)]',
        false:
          'border-dashed border-[var(--color-palettes-neutral-70-100)] bg-[var(--color-palettes-white-10-100)]',
      },
    },
  }
);

/** The glyph is decorative — the readout carries the value for assistive technology. */
export function FuelLevelValue(props: FuelLevelValueProps) {
  const {fraction, percent, isRecorded} = resolveLevel(props.value, props.min, props.max);
  const tone = props.tone ?? DEFAULT_TONE;

  return (
    <div className={cn(rootVariants())} data-testid={suffixTestId('fuelLevelValue', props)}>
      <div className={cn(glyphVariants())} aria-hidden>
        <Text size={EDGE_LABEL_SIZE} color={EMPTY_LABEL_COLOR[tone]} noWrap>
          {EMPTY_LABEL}
        </Text>
        <div
          className={cn(barVariants({isRecorded}))}
          style={{borderStyle: isRecorded ? 'solid' : 'dashed'}}
          data-testid={suffixTestId('bar', props)}
        >
          <Show when={isRecorded}>
            <LevelFill
              $fraction={fraction ?? 0}
              $bandColor={tone === 'neutral' ? NEUTRAL_FILL_COLOR : getLevelBandColor(percent)}
              $radius={BAR_RADIUS}
            />
          </Show>
        </div>
        <Text size={EDGE_LABEL_SIZE} color={FULL_LABEL_COLOR} noWrap>
          {FULL_LABEL}
        </Text>
      </div>
      <Text
        size={props.readoutSize ?? DEFAULT_READOUT_SIZE}
        color="secondary"
        noWrap
        data-testid={suffixTestId('value', props)}
      >
        {isRecorded ? formatPercent(percent) : EMPTY_PLACEHOLDER}
      </Text>
    </div>
  );
}
